import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { theme } from '../theme';
import { scrolls as scrollContent } from '../ScrollContent';
import confetti from 'canvas-confetti';
import {
  trackScrollReading,
  getScrollReadingsToday,
  getScrollProgress,
  ensureScrollProgressStarted,
  isHabitComplete,
  getActiveScrollId,
  switchActiveScroll,
  getSuggestedActiveScroll,
  computeDisplayQualifyingDays,
  checkScrollCompletion,
} from '../utils/progressTracker';
import { VoiceSelector } from './Audio/VoiceSelector';
import { MusicSelector } from './Audio/MusicSelector';
import { AudioPlayer } from './Audio/AudioPlayer';
import BadgeEarnedPopup from './BadgeEarnedPopup';

const MANDINO_NEXT = {
  habit:          { id: 'love',             message: "You have mastered the first law. Og Mandino's journey now calls you toward love — the greatest force known to mankind." },
  love:           { id: 'persistence',      message: "You have opened your heart. Now forge your will. Persistence is the scroll that separates those who dream from those who achieve." },
  persistence:    { id: 'selfEsteem',       message: "Your persistence is proven. Now cultivate the most powerful foundation of all — your belief in yourself." },
  selfEsteem:     { id: 'presence',         message: "You stand taller now. The next scroll asks you to live fully in this moment, where all power resides." },
  presence:       { id: 'emotionalMastery', message: "You have learned to be here. Now learn to feel deeply — and to master what you feel." },
  emotionalMastery: { id: 'joy',            message: "Your emotions serve you now. It is time to greet each day with laughter and an open heart." },
  joy:            { id: 'growth',           message: "Joy lives in you now. Turn that energy toward your endless capacity to grow and become more." },
  growth:         { id: 'action',           message: "You embrace becoming. Now act. The scroll of action is where transformation meets the world." },
  action:         { id: 'wisdom',           message: "You have learned to move. Now learn to listen — for wisdom whispers to those who are still enough to hear." },
};

function ScrollReader() {
  const { scrollId } = useParams();
  const navigate = useNavigate();
  
  const [scroll, setScroll] = useState(null);
  const [loading, setLoading] = useState(true);
  const [completedReadings, setCompletedReadings] = useState([]);
  const [voiceSelection, setVoiceSelection] = useState(null);
  const [musicSelection, setMusicSelection] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [scrollDayCount, setScrollDayCount] = useState(0);
  const [scrollQualifyingDays, setScrollQualifyingDays] = useState(0);
  const [scrollIsCompleted, setScrollIsCompleted] = useState(false);
  const [activeScrollId, setActiveScrollId] = useState(null);
  const [habitComplete, setHabitComplete] = useState(false);
  const [showSwitchConfirm, setShowSwitchConfirm] = useState(false);
  const [justCompletedScroll, setJustCompletedScroll] = useState(false);
  const [earnedBadge, setEarnedBadge] = useState(null);

  useEffect(() => {
    getCurrentUser();
    loadActiveScrollData();
  }, []);

  useEffect(() => {
    if (scrollId) {
      loadScroll();
      loadTodayProgress();
      loadScrollProgress();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scrollId]);

  const getCurrentUser = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    setCurrentUser(user);
  };

  const loadActiveScrollData = async () => {
    const active = await getActiveScrollId();
    const habitDone = await isHabitComplete();
    setActiveScrollId(active);
    setHabitComplete(habitDone);
  };

  const loadScroll = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('scrolls')
        .select('*')
        .eq('identifier', scrollId)
        .single();
      if (error) throw error;
      const contentFromFile = scrollContent[scrollId];
      const scrollData = {
        id: data.identifier,
        dbId: data.id,
        title: data.title,
        subtitle: data.subtitle,
        content: contentFromFile?.content || data.content,
        category: data.category,
        scrollSequence: data.scroll_sequence,
        isOriginalTen: data.is_original_ten,
      };
      setScroll(scrollData);
      await ensureScrollProgressStarted(data.identifier, data.id);
    } catch (error) {
      console.error('Error loading scroll:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadTodayProgress = async () => {
    const readings = await getScrollReadingsToday(scrollId);
    setCompletedReadings(readings);
  };

  const loadScrollProgress = async (todayReadings = null) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data, error } = await supabase
        .from('scroll_progress')
        .select('qualifying_days, last_reading_date, is_completed, is_active, completed_at, started_at, celebration_pending')
        .eq('user_id', user.id)
        .eq('scroll_id', scrollId)
        .single();

      if (error && error.code !== 'PGRST116') throw error;
      if (!data) return;

      await checkScrollCompletion(scrollId);

      const wasCompleted = scrollIsCompleted;
      const displayDays = computeDisplayQualifyingDays(data.qualifying_days, data.last_reading_date);
      setScrollDayCount(displayDays);
      setScrollQualifyingDays(data.qualifying_days);
      setScrollIsCompleted(data.is_completed);

      if (data.is_completed) {
        const readings = todayReadings ?? completedReadings;
        const justFinishedClean = !wasCompleted && readings.length >= 3;
        const pendingFromBefore = data.celebration_pending === true;

        if (justFinishedClean || pendingFromBefore) {
          setJustCompletedScroll(true);
          if (pendingFromBefore) {
            await supabase
              .from('scroll_progress')
              .update({ celebration_pending: false })
              .eq('user_id', user.id)
              .eq('scroll_id', scrollId);
          }
        }
      }
    } catch (error) {
      console.error('Error loading scroll progress:', error);
    }
  };

  const triggerConfettiBurst = () => {
    confetti({
      particleCount: 80,
      spread: 70,
      origin: { y: 0.6 },
      colors: ['#14b8a6', '#0ea5e9', '#ffffff'],
    });
  };

  const triggerFireworks = () => {
    const duration = 2000;
    const end = Date.now() + duration;
    const colors = ['#14b8a6', '#0ea5e9', '#ffd700', '#ffffff'];

    (function frame() {
      confetti({
        particleCount: 4,
        angle: 60,
        spread: 55,
        origin: { x: 0, y: 0.6 },
        colors,
      });
      confetti({
        particleCount: 4,
        angle: 120,
        spread: 55,
        origin: { x: 1, y: 0.6 },
        colors,
      });

      if (Date.now() < end) {
        requestAnimationFrame(frame);
      }
    })();
  };

  const handleReadingComplete = async (readingNumber) => {
    const isChecked = completedReadings.includes(readingNumber);

    // Update UI immediately (optimistic)
    if (isChecked) {
      setCompletedReadings(prev => prev.filter(r => r !== readingNumber));
    } else {
      setCompletedReadings(prev => [...prev, readingNumber]);
    }

    if (isChecked) {
      try {
        const today = new Date();
        const dateStr = `${today.getFullYear()}-${String(today.getMonth()+1).padStart(2,'0')}-${String(today.getDate()).padStart(2,'0')}`;
        await supabase
          .from('scroll_readings')
          .delete()
          .eq('user_id', currentUser.id)
          .eq('scroll_id', scrollId)
          .eq('reading_number', readingNumber)
          .eq('date', dateStr);
        await loadScrollProgress();
      } catch (error) {
        // Revert on failure
        setCompletedReadings(prev => [...prev, readingNumber]);
        console.error('Failed to uncheck reading:', error);
      }
      return;
    }

    try {
      const result = await trackScrollReading(scrollId, readingNumber);
      if (result?.success) {
        await loadScrollProgress([...completedReadings, readingNumber]);
        await loadActiveScrollData();
        if (result.newBadges?.length > 0) {
          setEarnedBadge(result.newBadges[0]);
        }

        if (readingNumber === 2) {
          triggerConfettiBurst();
        } else if (readingNumber === 3) {
          triggerFireworks();
        }
      } else {
        // Revert on failure
        setCompletedReadings(prev => prev.filter(r => r !== readingNumber));
      }
    } catch (error) {
      // Revert on failure
      setCompletedReadings(prev => prev.filter(r => r !== readingNumber));
      console.error('Failed to track reading:', error);
    }
  };

  const handleSwitchScroll = async () => {
    const success = await switchActiveScroll(scroll.id, scroll.dbId);
    if (success) {
      setActiveScrollId(scroll.id);
      setShowSwitchConfirm(false);
      await loadScrollProgress();
      await loadTodayProgress();
    }
  };

  const handleGoToNextScroll = async () => {
    const nextScrollId = await getSuggestedActiveScroll();
    if (nextScrollId && nextScrollId !== scrollId) {
      navigate(`/scroll/${nextScrollId}`);
    } else {
      navigate('/dashboard');
    }
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.error}>Loading scroll...</div>
      </div>
    );
  }

  if (!scroll) {
    return (
      <div style={styles.container}>
        <div style={styles.error}>Scroll not found</div>
      </div>
    );
  }

  const isThisScrollActive = scroll.id === activeScrollId;
  const isNonActiveScroll = !isThisScrollActive;
  const habitLocked = !habitComplete && scrollId !== 'habit';
  const canSwitchScrolls = habitComplete && isNonActiveScroll && !scrollIsCompleted;
  const mandinoNext = MANDINO_NEXT[scrollId];

  return (
    <div style={styles.container}>

      {earnedBadge && (
        <BadgeEarnedPopup
          badge={earnedBadge}
          onDismiss={() => setEarnedBadge(null)}
        />
      )}

      <div style={styles.header}>
        <div style={styles.headerButtons}>
          <button style={styles.backButton} onClick={() => navigate('/dashboard')}>
            ← Back to Dashboard
          </button>
          <button style={styles.progressButton} onClick={() => navigate('/progress')}>
            My Progress
          </button>
        </div>

        <div style={styles.titleRow}>
          <h1 style={styles.title}>{scroll.title}</h1>
          {scrollIsCompleted ? (
            <span style={styles.dayCounterCompleted}>30/30 Complete</span>
          ) : (
            <span style={styles.dayCounter}>Day {scrollDayCount} of 30</span>
          )}
        </div>

        {scroll.subtitle && (
          <p style={styles.subtitle}>{scroll.subtitle}</p>
        )}
      </div>

      {habitLocked && (
        <div style={styles.habitLockBanner}>
          <div style={styles.habitLockIcon}>📜</div>
          <div style={styles.habitLockText}>
            <strong style={{ color: theme.colors.textPrimary, fontSize: '1rem' }}>
              Your journey begins with the Scroll of Habit
            </strong>
            <p style={{ color: theme.colors.textSecondary, margin: '6px 0 0', fontSize: '0.875rem', lineHeight: '1.6' }}>
              Every transformation is built on a foundation of habit. Complete 30 days with the 
              Scroll of Habit before moving forward — this is not a restriction, it is the method. 
              Og Mandino knew this. Now you will too.
            </p>
          </div>
          <button style={styles.habitLockButton} onClick={() => navigate('/scroll/habit')}>
            Return to Habit
          </button>
        </div>
      )}

      {justCompletedScroll && (
        <div style={styles.completionBanner}>
          <div style={styles.completionIcon}>🏆</div>
          <div style={styles.completionText}>
            <strong style={{ color: theme.colors.textPrimary, fontSize: '1.125rem' }}>
              You have completed {scroll.title}!
            </strong>
            <p style={{ color: theme.colors.textSecondary, margin: '8px 0 0', fontSize: '0.875rem', lineHeight: '1.6' }}>
              30 days. Every reading an investment in who you are becoming. 
              This is what My Greatest Self is built on.
            </p>
            {mandinoNext && (
              <p style={{ color: theme.colors.teal, margin: '10px 0 0', fontSize: '0.875rem', lineHeight: '1.6', fontStyle: 'italic' }}>
                "{mandinoNext.message}"
              </p>
            )}
          </div>
          <div style={styles.completionButtons}>
            {mandinoNext && (
              <button style={styles.nextScrollButton} onClick={handleGoToNextScroll}>
                Begin Next Scroll
              </button>
            )}
            <button style={styles.completionDismiss} onClick={() => setJustCompletedScroll(false)}>
              Stay Here
            </button>
          </div>
        </div>
      )}

      {canSwitchScrolls && !justCompletedScroll && (
        <div style={styles.switchBanner}>
          <div style={styles.switchBannerContent}>
            <span style={styles.switchBannerIcon}>⚠️</span>
            <div style={styles.switchBannerText}>
              <strong>This is not your active scroll.</strong>
              <p style={styles.switchBannerSubtext}>
                Your daily progress tracking follows your active scroll. 
                Currently active:{' '}
                <strong style={{ color: theme.colors.teal }}>
                  {activeScrollId ? activeScrollId.charAt(0).toUpperCase() + activeScrollId.slice(1) : ''}
                </strong>
              </p>
            </div>
          </div>
          <button style={styles.switchButton} onClick={() => setShowSwitchConfirm(true)}>
            Switch to this scroll
          </button>
        </div>
      )}

      {showSwitchConfirm && (
        <div style={styles.modalOverlay} onClick={() => setShowSwitchConfirm(false)}>
          <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
            <h3 style={styles.modalTitle}>Switch Active Scroll?</h3>
            <p style={styles.modalText}>
              Switching to <strong>{scroll.title}</strong> will:
            </p>
            <ul style={styles.modalList}>
              <li>Reset this scroll's progress to Day 0</li>
              <li>Make this scroll your active scroll for daily tracking</li>
              <li>Keep your other scroll progress frozen (you can return later)</li>
            </ul>
            {scrollQualifyingDays > 0 && (
              <p style={styles.modalWarning}>
                This scroll currently has {scrollQualifyingDays} days of progress that will be reset.
              </p>
            )}
            <div style={styles.modalButtons}>
              <button style={styles.modalCancelButton} onClick={() => setShowSwitchConfirm(false)}>
                Cancel
              </button>
              <button style={styles.modalConfirmButton} onClick={handleSwitchScroll}>
                Yes, Switch Scroll
              </button>
            </div>
          </div>
        </div>
      )}

      <div style={styles.controls}>
        <div style={styles.audioSection}>
          <h3 style={styles.audioTitle}>Customize Your Experience</h3>
          <div style={styles.audioControls}>
            <VoiceSelector
              userId={currentUser?.id}
              scrollId={scroll.dbId}
              scrollTitle={scroll.title}
              scrollText={scroll.content}
              onVoiceChange={setVoiceSelection}
            />
            <MusicSelector
              userId={currentUser?.id}
              onMusicChange={setMusicSelection}
            />
          </div>
        </div>
      </div>

      {(voiceSelection?.url || musicSelection?.url) && (
        <div style={styles.playerContainer}>
          <AudioPlayer
            voiceUrl={voiceSelection?.url}
            musicUrl={musicSelection?.url}
            musicVolume={musicSelection?.volume !== undefined ? musicSelection.volume : 0.08}
            voiceVolume={voiceSelection?.volume || 1.0}
            onComplete={() => console.log('Audio playback complete')}
          />
        </div>
      )}

      <div style={styles.scrollContent}>
        <div style={styles.instruction}>
          Read this scroll three times today: morning, midday, and evening.
          Reading aloud with emotion is most powerful — a silent reading is always better than none.
        </div>
        <div style={styles.textContent}>
          {scroll.content.split('\n').filter(p => p.trim()).map((paragraph, index) => (
            <p key={index} style={styles.paragraph}>{paragraph}</p>
          ))}
        </div>
      </div>

      <div style={styles.tracker}>
        <div style={styles.trackerHeader}>
          <h3 style={styles.trackerTitle}>Today's Readings</h3>
          {scrollIsCompleted ? (
            <span style={styles.trackerCompletedBadge}>30/30</span>
          ) : (
            <span style={styles.trackerDayBadge}>Day {scrollDayCount} of 30</span>
          )}
        </div>

        {habitLocked ? (
          <div style={styles.lockedReadingMessage}>
            <span style={styles.lockedReadingIcon}>🔒</span>
            <p style={styles.lockedReadingText}>
              Complete 30 days with the Scroll of Habit to track readings here.
            </p>
          </div>
        ) : (
          <>
            <div style={styles.checkboxContainer}>
              {[
                { num: 1, label: 'Morning Reading',  emoji: '☀️' },
                { num: 2, label: 'Midday Reading',   emoji: '🌤️' },
                { num: 3, label: 'Evening Reading',  emoji: '🌙' },
              ].map(({ num, label, emoji }) => {
                const done = completedReadings.includes(num);
                return (
                  <div key={num} style={styles.checkbox}>
                    <input
                      type="checkbox"
                      id={`reading-${num}`}
                      checked={done}
                      onChange={() => handleReadingComplete(num)}
                      style={{ cursor: 'pointer' }}
                    />
                    <label
                      htmlFor={`reading-${num}`}
                      style={{ ...styles.checkboxLabel, color: done ? theme.colors.teal : theme.colors.textSecondary }}
                    >
                      {label} {emoji} {done && '✓'}
                    </label>
                  </div>
                );
              })}
            </div>

            {completedReadings.length >= 2 && completedReadings.length < 3 && (
              <p style={styles.qualifyingNote}>Today counts as a qualifying day toward your 30!</p>
            )}
            {completedReadings.length >= 3 && (
              <p style={styles.qualifyingNote}>Perfect day! All three readings complete.</p>
            )}
          </>
        )}
      </div>

      {completedReadings.length >= 1 && (
        <div style={styles.journalPromptSection}>
          <span style={styles.journalPromptIcon}>📝</span>
          <div style={styles.journalPromptTextWrap}>
            <h3 style={styles.journalPromptTitle}>Reflect on Your Reading</h3>
            <p style={styles.journalPromptSubtext}>
              Take a moment to journal your thoughts, feelings, and insights from today's reading.
            </p>
          </div>
          <button
            style={styles.journalPromptButton}
            onClick={() => navigate('/progress?tab=journal')}
          >
            Open Journal
          </button>
        </div>
      )}
    </div>
  );
}

const styles = {
  container: {
    minHeight: '100vh',
    backgroundColor: theme.colors.bgDark,
    padding: '20px',
    paddingBottom: '60px',
  },
  header: {
    maxWidth: '900px',
    margin: '0 auto 32px',
  },
  headerButtons: {
    display: 'flex',
    gap: '12px',
    marginBottom: '16px',
    flexWrap: 'wrap',
  },
  backButton: {
    backgroundColor: 'transparent',
    border: 'none',
    color: theme.colors.teal,
    fontSize: '0.875rem',
    cursor: 'pointer',
    padding: '0.5714em 0',
  },
  progressButton: {
    background: 'linear-gradient(135deg, #14b8a6 0%, #0ea5e9 100%)',
    border: 'none',
    borderRadius: '8px',
    padding: '0.5714em 1.1429em',
    color: '#0a0e1a',
    fontSize: '0.875rem',
    fontWeight: '600',
    cursor: 'pointer',
  },
  title: {
    fontSize: '2.25rem',
    fontWeight: '700',
    background: theme.colors.gradient,
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    marginBottom: '8px',
  },
  titleRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: '12px',
    marginBottom: '8px',
  },
  dayCounter: {
    display: 'inline-block',
    backgroundColor: `${theme.colors.blue}20`,
    border: `1px solid ${theme.colors.blue}60`,
    borderRadius: '20px',
    padding: '4px 16px',
    fontSize: '0.875rem',
    fontWeight: '600',
    color: theme.colors.blue,
    whiteSpace: 'nowrap',
    flexShrink: 0,
  },
  dayCounterCompleted: {
    display: 'inline-block',
    backgroundColor: `${theme.colors.teal}20`,
    border: `1px solid ${theme.colors.teal}60`,
    borderRadius: '20px',
    padding: '4px 16px',
    fontSize: '0.875rem',
    fontWeight: '600',
    color: theme.colors.teal,
    whiteSpace: 'nowrap',
    flexShrink: 0,
  },
  subtitle: {
    color: theme.colors.textSecondary,
    fontSize: '1.125rem',
    marginTop: '8px',
  },
  habitLockBanner: {
    maxWidth: '900px',
    margin: '0 auto 24px',
    background: 'linear-gradient(135deg, rgba(99,102,241,0.12) 0%, rgba(20,184,166,0.12) 100%)',
    border: `2px solid ${theme.colors.blue}`,
    borderRadius: '16px',
    padding: '24px 28px',
    display: 'flex',
    alignItems: 'flex-start',
    gap: '20px',
    flexWrap: 'wrap',
  },
  habitLockIcon: { fontSize: '2.25rem', flexShrink: 0, marginTop: '2px' },
  habitLockText: { flex: 1, minWidth: '220px' },
  habitLockButton: {
    background: theme.colors.gradient,
    border: 'none',
    borderRadius: '10px',
    padding: '0.8em 1.6em',
    color: 'white',
    fontSize: '0.9375rem',
    fontWeight: '700',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    flexShrink: 0,
    alignSelf: 'center',
  },
  completionBanner: {
    maxWidth: '900px',
    margin: '0 auto 24px',
    background: 'linear-gradient(135deg, rgba(20,184,166,0.15) 0%, rgba(14,165,233,0.15) 100%)',
    border: `2px solid ${theme.colors.teal}`,
    borderRadius: '16px',
    padding: '28px',
    display: 'flex',
    alignItems: 'flex-start',
    gap: '20px',
    flexWrap: 'wrap',
  },
  completionIcon: { fontSize: '2.75rem', flexShrink: 0 },
  completionText: { flex: 1, minWidth: '220px' },
  completionButtons: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
    flexShrink: 0,
    alignSelf: 'center',
  },
  nextScrollButton: {
    background: theme.colors.gradient,
    border: 'none',
    borderRadius: '10px',
    padding: '0.8em 1.6em',
    color: 'white',
    fontSize: '0.9375rem',
    fontWeight: '700',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  },
  completionDismiss: {
    backgroundColor: 'transparent',
    border: `1px solid ${theme.colors.bgLight}`,
    borderRadius: '10px',
    padding: '0.7143em 1.7143em',
    color: theme.colors.textSecondary,
    fontSize: '0.875rem',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  },
  switchBanner: {
    maxWidth: '900px',
    margin: '0 auto 24px',
    backgroundColor: '#ff980015',
    border: '2px solid #ff9800',
    borderRadius: '12px',
    padding: '20px 24px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '20px',
    flexWrap: 'wrap',
  },
  switchBannerContent: {
    display: 'flex',
    alignItems: 'flex-start',
    flexWrap: 'wrap',
    gap: '16px',
    flex: 1,
    minWidth: '250px',
  },
  switchBannerIcon: { fontSize: '1.75rem', lineHeight: 1, flexShrink: 0 },
  switchBannerText: { flex: 1, color: theme.colors.textPrimary, fontSize: '0.9375rem', lineHeight: '1.5' },
  switchBannerSubtext: { margin: '8px 0 0 0', fontSize: '0.875rem', color: theme.colors.textSecondary },
  switchButton: {
    background: theme.colors.gradient,
    border: 'none',
    borderRadius: '8px',
    padding: '0.8em 1.6em',
    color: 'white',
    fontSize: '0.9375rem',
    fontWeight: '600',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    flexShrink: 0,
  },
  modalOverlay: {
    position: 'fixed',
    top: 0, left: 0, right: 0, bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.8)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    padding: '20px',
  },
  modal: {
    backgroundColor: theme.colors.bgMedium,
    borderRadius: '16px',
    padding: '32px',
    maxWidth: '500px',
    width: '100%',
    border: `2px solid ${theme.colors.bgLight}`,
  },
  modalTitle: { color: theme.colors.textPrimary, fontSize: '1.5rem', fontWeight: '700', margin: '0 0 16px 0' },
  modalText: { color: theme.colors.textSecondary, fontSize: '1rem', lineHeight: '1.6', marginBottom: '16px' },
  modalList: { color: theme.colors.textSecondary, fontSize: '0.9375rem', lineHeight: '1.8', marginBottom: '16px', paddingLeft: '24px' },
  modalWarning: {
    backgroundColor: '#ff980015',
    border: '1px solid #ff9800',
    borderRadius: '8px',
    padding: '12px 16px',
    color: '#ff9800',
    fontSize: '0.875rem',
    fontWeight: '500',
    marginBottom: '24px',
  },
  modalButtons: { display: 'flex', gap: '12px', justifyContent: 'flex-end', flexWrap: 'wrap', marginTop: '24px' },
  modalCancelButton: {
    backgroundColor: 'transparent',
    border: `2px solid ${theme.colors.bgLight}`,
    color: theme.colors.textSecondary,
    borderRadius: '8px',
    padding: '0.8em 1.6em',
    fontSize: '0.9375rem',
    fontWeight: '600',
    cursor: 'pointer',
  },
  modalConfirmButton: {
    background: theme.colors.gradient,
    border: 'none',
    borderRadius: '8px',
    padding: '0.8em 1.6em',
    color: 'white',
    fontSize: '0.9375rem',
    fontWeight: '600',
    cursor: 'pointer',
  },
  controls: { maxWidth: '900px', margin: '0 auto 24px' },
  audioSection: { backgroundColor: theme.colors.bgMedium, borderRadius: '12px', padding: '24px' },
  audioTitle: { color: theme.colors.textPrimary, fontSize: '1.25rem', fontWeight: '600', marginBottom: '20px' },
  audioControls: { display: 'flex', flexDirection: 'column', gap: '20px' },
  playerContainer: { maxWidth: '900px', margin: '0 auto 24px' },
  scrollContent: {
    maxWidth: '900px',
    margin: '0 auto 32px',
    backgroundColor: theme.colors.bgMedium,
    borderRadius: '12px',
    padding: '40px',
  },
  instruction: {
    color: theme.colors.teal,
    fontSize: '0.875rem',
    fontWeight: '600',
    marginBottom: '24px',
    padding: '12px',
    backgroundColor: theme.colors.bgLight,
    borderRadius: '8px',
    textAlign: 'center',
  },
  textContent: { color: theme.colors.textPrimary, fontSize: '1.125rem', lineHeight: '1.8' },
  paragraph: { marginBottom: '24px' },
  tracker: {
    maxWidth: '900px',
    margin: '0 auto',
    backgroundColor: theme.colors.bgMedium,
    borderRadius: '12px',
    padding: '32px',
  },
  trackerHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '20px',
    flexWrap: 'wrap',
    gap: '8px',
  },
  trackerTitle: { color: theme.colors.textPrimary, fontSize: '1.25rem', fontWeight: '600', margin: 0 },
  trackerDayBadge: {
    backgroundColor: `${theme.colors.blue}20`,
    border: `1px solid ${theme.colors.blue}60`,
    borderRadius: '20px',
    padding: '4px 14px',
    fontSize: '0.8125rem',
    fontWeight: '600',
    color: theme.colors.blue,
  },
  trackerCompletedBadge: {
    backgroundColor: `${theme.colors.teal}20`,
    border: `1px solid ${theme.colors.teal}60`,
    borderRadius: '20px',
    padding: '4px 14px',
    fontSize: '0.8125rem',
    fontWeight: '600',
    color: theme.colors.teal,
  },
  lockedReadingMessage: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: '14px',
    padding: '20px',
    backgroundColor: theme.colors.bgLight,
    borderRadius: '10px',
  },
  lockedReadingIcon: { fontSize: '1.5rem', flexShrink: 0 },
  lockedReadingText: { color: theme.colors.textSecondary, fontSize: '0.875rem', lineHeight: '1.5', margin: 0 },
  checkboxContainer: { display: 'flex', flexDirection: 'column', gap: '16px' },
  checkbox: { display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '12px' },
  checkboxLabel: { fontSize: '1rem', cursor: 'pointer' },
  qualifyingNote: {
    marginTop: '20px',
    padding: '12px 16px',
    backgroundColor: `${theme.colors.teal}15`,
    border: `1px solid ${theme.colors.teal}40`,
    borderRadius: '8px',
    color: theme.colors.teal,
    fontSize: '0.875rem',
    fontWeight: '500',
  },
  error: { color: theme.colors.textPrimary, fontSize: '1.25rem', textAlign: 'center', marginTop: '100px' },

  journalPromptSection: {
    maxWidth: '900px',
    margin: '24px auto 0',
    backgroundColor: theme.colors.bgMedium,
    borderRadius: '12px',
    padding: '24px 28px',
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: '20px',
    border: `1px solid ${theme.colors.bgLight}`,
  },
  journalPromptIcon: {
    fontSize: '2rem',
    flexShrink: 0,
  },
  journalPromptTextWrap: {
    flex: 1,
    minWidth: '220px',
  },
  journalPromptTitle: {
    color: theme.colors.textPrimary,
    fontSize: '1.125rem',
    fontWeight: '700',
    margin: '0 0 6px 0',
  },
  journalPromptSubtext: {
    color: theme.colors.textSecondary,
    fontSize: '0.875rem',
    lineHeight: '1.5',
    margin: 0,
  },
  journalPromptButton: {
    background: theme.colors.gradient,
    border: 'none',
    borderRadius: '10px',
    padding: '0.8em 1.6em',
    color: 'white',
    fontSize: '0.9375rem',
    fontWeight: '700',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    flexShrink: 0,
  },
};

export default ScrollReader;